import React, { useState, useMemo, useCallback, memo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Tooltip,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Edit as EditIcon,
  Save,
  Remove,
  Download
} from '@mui/icons-material';
import { publicApiRequest } from '../../../common/common';
import { useParams } from 'react-router-dom';
import { showNotification } from '../../../common/Notification/ToastifyNotification';
import moment from 'moment';

// Simple debounce hook
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

const RequirementRow = memo(({
  requirement,
  onUpdate,
  onRemove,
  section,
  isPublic,
  isViewMode,
  formId
}) => {
  const [uploading, setUploading] = useState(false);
  const { formid } = useParams();
  // Create local state for all editable fields
  const [localData, setLocalData] = useState({
    description: requirement.description || '',
    response: requirement.response || '',
    remarks: requirement.remarks || '',
    responseType: requirement.responseType || 'dropdown',
    attachment: requirement.attachment || false
  });

  // Initialize local state when requirement prop changes
  React.useEffect(() => {
    setLocalData({
      description: requirement.description || '',
      response: requirement.response || '',
      remarks: requirement.remarks || '',
      responseType: requirement.responseType || 'dropdown',
      attachment: requirement.attachment || false
    });
  }, [requirement.id]); // Only reset when requirement ID changes

  const debouncedUpdate = useDebounce((field, value) => {
    onUpdate(section.id, requirement.id, field, value);
  }, 200);

  const handleChange = useCallback((field, value) => {
    // Update local state immediately for responsive UI
    setLocalData(prev => ({ ...prev, [field]: value }));
    // Debounce the parent update
    debouncedUpdate(field, value);
  }, [debouncedUpdate, requirement.id]);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type and size
    const validFileTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validFileTypes.includes(file.type)) {
      alert('Please upload PDF, DOC, DOCX, JPG, or PNG files only.');
      return;
    }

    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('public_files', file);
      const response = await publicApiRequest('post', '/public-file-upload', formData);
      onUpdate(section.id, requirement.id, 'attachments', [...(requirement.attachments || []), response?.data?.data?.[0]]);

    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }, [requirement.id, requirement.attachments, onUpdate]);

  const handleRemoveAttachment = useCallback(async (attachmentId) => {
    const updatedAttachments = requirement.attachments?.filter(att => att.id !== attachmentId) || [];
    onUpdate(section.id, requirement.id, 'attachments', updatedAttachments);
  }, [requirement.id, requirement.attachments, onUpdate]);

  const handleResponseTypeChange = useCallback((e) => {
    const value = e.target.value;
    setLocalData(prev => ({ ...prev, responseType: value }));
    onUpdate(section.id, requirement.id, 'responseType', value);
  }, [requirement.id, onUpdate]);

  const handleAttachmentChange = useCallback((e) => {
    const value = e.target.value === 'yes';
    setLocalData(prev => ({ ...prev, attachment: value }));
    onUpdate(section.id, requirement.id, 'attachment', value);
  }, [requirement.id, onUpdate]);

  return (
    <TableRow>
      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
        {(section.sectionName !== 'Attachments' && !isPublic && !isViewMode) && (
          <Tooltip title="Remove Field">
            <IconButton size="small" color="error" onClick={() => onRemove(requirement.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0' }}>{requirement.code}</TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
        {section.sectionName !== 'Attachments' && !isPublic && !isViewMode ? (
          <TextField
            fullWidth size="small" multiline rows={3}
            value={localData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            variant="outlined" placeholder="Add required description here"
          />
        ) : requirement.description}
      </TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
        {(section.sectionName === 'Attachments' && !isPublic && !isViewMode) ? (
          <Box sx={{ position: 'relative' }}>
            <input
              accept=".pdf"
              style={{ display: 'none' }}
              id={`file-upload-${section.id}-${requirement.id}`}
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            {requirement.attachments.length === 0 && (
              <label htmlFor={`file-upload-${section.id}-${requirement.id}`}>
                <IconButton
                  component="span"
                  size="small"
                  color="primary"
                  disabled={uploading}
                >
                  <AttachFileIcon />
                </IconButton>
              </label>
            )}

            {uploading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Display uploaded files */}
            {requirement.attachments?.map((attachment) => (
              <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={`Download ${attachment.fileName}`}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const fileUrl = `${process.env.REACT_APP_IMAGEURL}bbn=${attachment.sortUrl}&uid=${formid || formId}`;
                      const link = document.createElement('a');
                      link.href = fileUrl;
                      link.target = '_blank';  // Open in new tab
                      link.rel = 'noopener noreferrer'; // Security best practice
                      link.download = attachment.fileName;
                      link.click();
                    }}
                  >
                    <img src="/download.png" alt="download" style={{ width: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Remove ${attachment.fileName}`}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        ) : section.sectionName === 'Attachments' && requirement.attachments.length > 0 ? requirement.attachments?.map((attachment) => (
          <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={`Download ${attachment.fileName}`}>
              <IconButton
                size="small"
                onClick={() => {
                  const fileUrl = `${process.env.REACT_APP_IMAGEURL}bbn=${attachment.sortUrl}&uid=${formid || formId}`;
                  const link = document.createElement('a');
                  link.href = fileUrl;
                  link.target = '_blank';  // Open in new tab
                  link.rel = 'noopener noreferrer'; // Security best practice
                  link.download = attachment.fileName;
                  link.click();
                }}
              >
                <img src="/download.png" alt="download" style={{ width: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )) : section.sectionName === 'Attachments' && (
          <Typography variant='body2' style={{ fontSize: 8 }}>Nothing to download</Typography>
        )}
      </TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0', background: isPublic && section.sectionName === 'Attachments' ? 'lightgray' : 'white' }}>
        {section.sectionName !== 'Attachments' && (
          <>
            {(!isPublic && !isViewMode) && (
              <FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 10, marginTop: 10 }}>
                <InputLabel>Response Type</InputLabel>
                <Select
                  value={localData.responseType}
                  onChange={handleResponseTypeChange}
                  label="Response Type"
                >
                  <MenuItem value="dropdown">Dropdown</MenuItem>
                  <MenuItem value="textfield">Text Field</MenuItem>
                </Select>
              </FormControl>
            )}

            {localData.responseType === 'textfield' ? (
              <TextField
                fullWidth size="small"
                value={localData.response}
                onChange={(e) => handleChange('response', e.target.value)}
                placeholder="Enter Your Response" variant="outlined" style={{ marginBottom: 10 }}
                disabled={isViewMode}
              />
            ) : (
              <FormControl fullWidth size="small">
                <Select
                  value={localData.response}
                  onChange={(e) => handleChange('response', e.target.value)}
                  displayEmpty variant="outlined" style={{ marginBottom: 10 }}
                  disabled={isViewMode}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Refer to Remarks">Refer to Remarks</MenuItem>
                </Select>
              </FormControl>
            )}
          </>
        )}
      </TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0', background: isPublic && section.sectionName === 'Attachments' ? 'lightgray' : 'white' }}>
        {section.sectionName !== 'Attachments' && (
          <TextField
            fullWidth size="small" multiline rows={2}
            value={localData.remarks}
            onChange={(e) => handleChange('remarks', e.target.value)}
            variant="outlined" placeholder="Enter remarks"
            disabled={isViewMode}
          />)}
      </TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0', background: isPublic && section.sectionName === 'Attachments' ? 'lightgray' : 'white' }}>
        {section.sectionName !== 'Attachments' && !isPublic && !isViewMode && (
          <FormControl fullWidth size="small">
            <Select
              value={localData.attachment ? 'yes' : 'no'}
              onChange={handleAttachmentChange}
              displayEmpty variant="outlined"
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>)}
        {localData.attachment && (
          (section.sectionName !== 'Attachments' && isPublic) ? (
            <Box sx={{ position: 'relative' }}>
              <input
                accept=".pdf"
                style={{ display: 'none' }}
                id={`file-upload-${section.id}-${requirement.id}`}
                type="file"
                onChange={handleFileUpload}
                disabled={uploading}
              />
              {requirement.attachments.length === 0 && (
                <label htmlFor={`file-upload-${section.id}-${requirement.id}`}>
                  <IconButton
                    component="span"
                    size="small"
                    color="primary"
                    disabled={uploading}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </label>
              )}

              {uploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* Display uploaded files */}
              {requirement.attachments?.map((attachment) => (
                <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={`Download ${attachment.fileName}`}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const fileUrl = `${process.env.REACT_APP_IMAGEURL}bbn=${attachment.sortUrl}&uid=${formid || formId}`;
                        const link = document.createElement('a');
                        link.href = fileUrl;
                        link.target = '_blank';  // Open in new tab
                        link.rel = 'noopener noreferrer'; // Security best practice
                        link.download = attachment.fileName;
                        link.click();
                      }}
                    >
                      <img src='/download.png' alt='download' style={{ width: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Remove ${attachment.fileName}`}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          ) : section.sectionName !== 'Attachments' && requirement.attachments.length > 0 && requirement.attachments?.map((attachment) => (
            <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={`Download ${attachment.fileName}`}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const fileUrl = `${process.env.REACT_APP_IMAGEURL}bbn=${attachment.sortUrl}&uid=${formid || formId}`;
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.target = '_blank';  // Open in new tab
                    link.rel = 'noopener noreferrer'; // Security best practice
                    link.download = attachment.fileName;
                    link.click();
                  }}
                >
                  <img src='/download.png' alt='download' style={{ width: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))
        )}
      </TableCell>
    </TableRow>
  );
});

// Simple Section Component with safe array access
const SectionComponent = memo(({
  section,
  onUpdateSection,
  onRemoveSection,
  onUpdateRequirement,
  onRemoveRequirement,
  onAddRequirement,
  isPublic,
  isViewMode,
  formId
}) => {
  const [localName, setLocalName] = useState(section.sectionName || '');

  const debouncedUpdate = useDebounce((value) => {
    onUpdateSection(section.id, value);
  }, 200);

  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setLocalName(value);
    debouncedUpdate(value);
  }, [debouncedUpdate]);

  if (section.type === 'purpose') return null;

  // Safe array access for requirements
  const requirements = section.requirements || [];

  return (
    <>
      <TableRow>
        <TableCell sx={{ border: '1px solid #e0e0e0' }}>
          {(section.sectionName !== 'Attachments' && !isPublic && !isViewMode) && (
            <Tooltip title="Remove Section">
              <IconButton size="small" color="error" onClick={() => onRemoveSection(section.id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold' }}>{section.title}</TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold' }} colSpan={5}>
          {section.isdefault || isPublic || isViewMode ? (
            section.sectionName
          ) : (
            <TextField
              size="small" value={localName} onChange={handleNameChange}
              placeholder="Enter section name" variant="outlined"
            />
          )}
        </TableCell>
      </TableRow>

      {requirements.map((requirement) => (
        <RequirementRow
          key={requirement.id}
          requirement={requirement}
          onUpdate={onUpdateRequirement}
          onRemove={onRemoveRequirement}
          section={section}
          isPublic={isPublic}
          isViewMode={isViewMode}
          formId={formId}
        />
      ))}

      {(section.sectionName !== 'Attachments' && !isPublic && !isViewMode) && (
        <TableRow>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }} colSpan={6}>
            <Button
              size="small" startIcon={<AddIcon />}
              onClick={() => onAddRequirement(section.id)}
              variant="outlined" sx={{ mt: 1, mb: 1 }}
            >
              ADD New Field
            </Button>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

// Progressive loading accordion content with safe data access
const AccordionContent = memo(({
  item,
  onItemUpdate,
  isPublic,
  setFormData,
  isViewMode,
  formId
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [localPurpose, setLocalPurpose] = useState(
    item.sections?.find(s => s.type === 'purpose')?.description || ''
  );

  // Simulate progressive loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100); // Small delay to show loader

    return () => clearTimeout(timer);
  }, []);

  const debouncedPurposeUpdate = useDebounce((value) => {
    onItemUpdate('purpose', value);
  }, 200);

  const handlePurposeChange = useCallback((e) => {
    const value = e.target.value;
    setLocalPurpose(value);
    debouncedPurposeUpdate(value);
  }, [debouncedPurposeUpdate]);

  const handleAddSection = useCallback(() => {
    onItemUpdate('addSection');
  }, [onItemUpdate]);

  const handleUpdateSection = useCallback((sectionId, value) => {
    onItemUpdate('updateSection', { sectionId, value });
  }, [onItemUpdate]);

  const handleRemoveSection = useCallback((sectionId) => {
    onItemUpdate('removeSection', sectionId);
  }, [onItemUpdate]);

  const handleUpdateRequirement = useCallback((sectionId, requirementId, field, value) => {
    onItemUpdate('updateRequirement', { sectionId, requirementId, field, value });
  }, [onItemUpdate]);

  const handleRemoveRequirement = useCallback((requirementId) => {
    onItemUpdate('removeRequirement', requirementId);
  }, [onItemUpdate]);

  const handleAddRequirement = useCallback((sectionId) => {
    onItemUpdate('addRequirement', sectionId);
  }, [onItemUpdate]);

  // Safe array access for sections
  const sections = item.sections || [];
  const customSections = sections.filter(s => s.type !== 'purpose');

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Table size="small" sx={{ minWidth: 800 }}>
      <TableBody>
        {/* Simple static rows */}
        <TableRow>
          <TableCell sx={{ border: '1px solid #e0e0e0', width: '6.5%' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', width: '5.7%' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold', width: '32.3%' }}>Mfg</TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', width: '5%' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', width: '19.2%' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Your response here"
              variant="outlined"
              value={item?.manufacturer}
              disabled={isViewMode}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  items: prev.items.map(i =>
                    i.id === item.id
                      ? { ...i, manufacturer: e.target.value }
                      : i
                  )
                }));
              }}
            />
          </TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', width: '23.1%' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', width: '10%' }}></TableCell>
        </TableRow>

        <TableRow>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold' }}>Model</TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Your response here"
              variant="outlined"
              value={item?.model}
              disabled={isViewMode}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  items: prev.items.map(i =>
                    i.id === item.id
                      ? { ...i, model: e.target.value }
                      : i
                  )
                }));
              }}
            />
          </TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
        </TableRow>

        {/* Purpose Section */}
        <TableRow>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold' }}>PURPOSE</TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}>
            {isPublic || isViewMode ? (
              localPurpose
            ) : (
              <TextField
                fullWidth size="small" multiline rows={3} value={localPurpose}
                onChange={handlePurposeChange} variant="outlined"
                placeholder="Add required description here"
              />
            )}
          </TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', background: isPublic ? 'lightgray' : 'white' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', background: isPublic ? 'lightgray' : 'white' }}></TableCell>
          <TableCell sx={{ border: '1px solid #e0e0e0', background: isPublic ? 'lightgray' : 'white' }}></TableCell>
        </TableRow>

        {/* Render sections progressively with safe access */}
        {customSections.map((section) => (
          <SectionComponent
            key={section.id}
            section={section}
            onUpdateSection={handleUpdateSection}
            onRemoveSection={handleRemoveSection}
            onUpdateRequirement={handleUpdateRequirement}
            onRemoveRequirement={handleRemoveRequirement}
            onAddRequirement={handleAddRequirement}
            isPublic={isPublic}
            isViewMode={isViewMode}
            formId={formId}
          />
        ))}

        {/* Add Section Button */}
        {(!isPublic && !isViewMode) && (
          <TableRow>
            <TableCell sx={{ border: '1px solid #e0e0e0' }} colSpan={7}>
              <Button
                startIcon={<AddIcon />} onClick={handleAddSection}
                variant="contained" size="small" sx={{ mt: 1, mb: 1 }}
              >
                Add New Section
              </Button>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
});

// Main Item Component with loading state and auto-close functionality
const ItemComponent = memo(({
  item,
  isExpanded,
  onToggle,
  onItemAction,
  scrollToItem,
  isPublic,
  setFormData,
  isViewMode,
  formId
}) => {
  const itemRef = useRef(null);

  const handleToggle = useCallback((event, expanded) => {
    onToggle(item.id, expanded);

    // Scroll to item when expanded
    if (expanded && itemRef.current) {
      setTimeout(() => {
        scrollToItem(itemRef.current);
      }, 100);
    }
  }, [item.id, onToggle, scrollToItem]);

  const handleItemUpdate = useCallback((action, data) => {
    onItemAction(item.id, action, data);
  }, [item.id, onItemAction]);

  return (
    <>
      {/* Item Summary Row */}
      <TableRow ref={itemRef}>
        <TableCell sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Accordion
            expanded={isExpanded}
            onChange={handleToggle}
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              backgroundColor: 'transparent',
              minWidth: 'auto',
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <AccordionSummary
              expandIcon={
                isExpanded ? (
                  <Remove fontSize="small" color='secondary' />
                ) : (
                  <AddIcon fontSize="small" color='primary' />
                )
              }
              sx={{
                minHeight: 'auto !important',
                padding: 0,
                '& .MuiAccordionSummary-content': {
                  margin: '0 !important',
                  display: 'none'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  transform: 'none',
                  transition: 'transform 0.3s ease-in-out'
                }
              }}
            />
          </Accordion>
          {item.srNo}
        </TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold' }}>{item.name}</TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 'bold' }}>{item.quantity}</TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
        <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
      </TableRow>

      {/* Accordion Details */}
      <TableRow>
        <TableCell colSpan={7} sx={{ padding: 0 }}>
          <Accordion
            expanded={isExpanded}
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              margin: 0,
              transition: 'all 0.3s ease-in-out',
              '&.MuiAccordion-root': {
                transition: 'all 0.3s ease-in-out'
              }
            }}
          >
            <AccordionSummary sx={{ display: 'none' }} />
            <AccordionDetails
              sx={{
                padding: 0,
                transition: 'all 0.3s ease-in-out'
              }}
            >
              {isExpanded && (
                <AccordionContent
                  item={item}
                  onItemUpdate={handleItemUpdate}
                  isPublic={isPublic}
                  setFormData={setFormData}
                  isViewMode={isViewMode}
                  formId={formId}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </TableCell>
      </TableRow>
    </>
  );
});

const generateResponseDocument = (formData, projectData) => {
  console.log(formData);
  const formatItemTable = (item) => {
    let html = '';

    // Item header row (always shown)
    html += `
      <tr>
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">${item.srNo || ''}</td>
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;" />
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">${item.name || ''}</td>
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">${item.quantity || ''}</td>
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;" />
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;" />
      </tr>
    `;

    // Manufacturer and Model rows
    html += `
      <tr>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">Mfg</td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px;">${item.manufacturer || ''}</td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
      </tr>
      <tr>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">Model</td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px;">${item.model || ''}</td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
        <td style="border: 1px solid #000; padding: 8px;"></td>
      </tr>
    `;

    // Find purpose section
    const purposeSection = item.sections?.find(s => s.type === 'purpose');
    if (purposeSection) {
      html += `
        <tr>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">PURPOSE</td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
        </tr>
        <tr>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px;">${purposeSection.description || ''}</td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
          <td style="border: 1px solid #000; padding: 8px; background-color: ${formData.isPublic ? '#f0f0f0' : 'white'}"></td>
          <td style="border: 1px solid #000; padding: 8px; background-color: ${formData.isPublic ? '#f0f0f0' : 'white'}"></td>
          <td style="border: 1px solid #000; padding: 8px; background-color: ${formData.isPublic ? '#f0f0f0' : 'white'}"></td>
        </tr>
      `;
    }

    // Custom sections
    const customSections = item.sections?.filter(s => s.type !== 'purpose') || [];

    customSections.forEach(section => {
      // Section header row
      html += `
        <tr>
          <td style="border: 1px solid #000; padding: 8px;">
            ${!section.isdefault && formData.isPublic ? '' : ''}
          </td>
          <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">
            ${section.title || ''}
          </td>
          <td style="border: 1px solid #000; padding: 8px; font-weight: bold;" colspan="5">
            ${section.sectionName || ''}
          </td>
        </tr>
      `;

      // Requirements in this section
      section.requirements?.forEach(req => {
        html += `
          <tr>
            <td style="border: 1px solid #000; padding: 8px;"></td>
            <td style="border: 1px solid #000; padding: 8px;">${req.code || ''}</td>
            <td style="border: 1px solid #000; padding: 8px;">${req.description || ''}</td>
            <td style="border: 1px solid #000; padding: 8px;">
              ${section.sectionName === 'Attachments' && req.attachments?.length > 0
            ? req.attachments.map(a => `${a.fileName}`).join('<br>')
            : ''}
            </td>
            <td style="border: 1px solid #000; padding: 8px; background-color: ${section.sectionName === 'Attachments' && formData.isPublic ? '#f0f0f0' : 'white'}">
              ${section.sectionName !== 'Attachments' ? (req.response || '') : ''}
            </td>
            <td style="border: 1px solid #000; padding: 8px; background-color: ${section.sectionName === 'Attachments' && formData.isPublic ? '#f0f0f0' : 'white'}">
              ${section.sectionName !== 'Attachments' ? (req.remarks || '') : ''}
            </td>
            <td style="border: 1px solid #000; padding: 8px; background-color: ${section.sectionName === 'Attachments' && formData.isPublic ? '#f0f0f0' : 'white'}">
              ${req.attachment && req.attachments?.length > 0
            ? req.attachments.map(a => `${a.fileName}`).join('<br>')
            : ''}
            </td>
          </tr>
        `;
      });
    });

    return html;
  };

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Technical Response - ${formData.bidName || 'Bidding Form'}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
        }
        .container {
            background-color: white;
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #3f51b5;
        }
        .header h1 {
            color: #3f51b5;
            margin: 0 0 10px 0;
        }
        .header h2 {
            color: #666;
            margin: 0;
            font-weight: normal;
        }
        .bid-info {
            margin-bottom: 30px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
            font-size: 12px;
            table-layout: fixed;
        }
        th, td {
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
        }
        th {
            background-color: #e3f2fd;
            font-weight: bold;
        }
        .section-title {
            font-weight: bold;
            padding: 10px;
            background-color: #f5f5f5;
            border-left: 4px solid #3f51b5;
            margin: 20px 0 10px 0;
        }
        .ack-section {
            background-color: #e8f5e8;
            padding: 15px;
            border-radius: 4px;
            margin: 20px 0;
        }
        .signature-section {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #3f51b5;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .col-1 { width: 6%; }
        .col-2 { width: 5.7%; }
        .col-3 { width: 32.3%; }
        .col-4 { width: 5%; }
        .col-5 { width: 19.2%; }
        .col-6 { width: 23.1%; }
        .col-7 { width: 8.7%; }
        .print-info {
            text-align: right;
            font-size: 11px;
            color: #666;
            margin-bottom: 10px;
        }
        .no-data {
            color: #999;
            font-style: italic;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="print-info">
            Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
        </div>
        
        <div class="header">
            <h1>Technical Specification Form</h1>
        </div>
        
        <!-- Bid Information Table -->
        <div class="bid-info">
            <table>
                <thead>
                    <tr>
                        <th class="col-1">BID GROUP</th>
                        <th class="col-2">BID NO</th>
                        <th class="col-3">Bid Name</th>
                        <th class="col-5">Vendor Response</th>
                        <th class="col-6">Vendor Remarks in details</th>
                        <th class="col-7">Vendor Attachments</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>${formData.bidGroup || ''}</td>
                        <td>${formData.bidNo || ''}</td>
                        <td>${formData.bidName || ''}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <!-- Main Items Table -->
        <div class="section-title">Items Specification</div>
        <table>
            <thead>
                <tr>
                    <th class="col-1">Sr. No</th>
                    <th class="col-2"></th>
                    <th class="col-3">Name of Item</th>
                    <th class="col-4">Qty</th>
                    <th class="col-5"></th>
                    <th class="col-6"></th>
                    <th class="col-7"></th>
                </tr>
            </thead>
            <tbody>
                ${formData.items?.map(item => formatItemTable(item)).join('') ||
    '<tr><td colspan="7" class="no-data">No items available</td></tr>'}
            </tbody>
        </table>
        
        <!-- Vendor Documents Section -->
        ${formData.vendorDocuments?.length > 0 ? `
            <div class="section-title">Vendor Documentation Attachment</div>
            <table>
                <thead>
                    <tr>
                        <th style="width: 90%;">Document Name</th>
                        <th style="width: 10%;">Attachments</th>
                    </tr>
                </thead>
                <tbody>
                    ${formData.vendorDocuments.map(doc => `
                        <tr>
                            <td>${doc.name || 'Unnamed Document'}</td>
                            <td>
                                ${doc.attachments?.map(att =>
      `<div></span>${att.fileName}</div>`
    ).join('') || '<span class="no-data">No files</span>'}
                            </td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        ` : ''}
        
        <!-- Acknowledgements Section -->
        <div class="section-title">Acknowledgement</div>
        <div class="ack-section">
            <p>${formData.acknowledgement?.reviewed ? '✓' : '✗'} 
               ${formData.acknowledgement?.reviewedLabel || 'I confirm that I have received, reviewed, and understood the RFP...'}</p>
            
            <p>${formData.acknowledgement?.accepted ? '✓' : '✗'} 
               I accept the terms and conditions unless otherwise stated...</p>
        </div>
        
        <!-- Place and Signature -->
        <div class="signature-section">
            <p><strong>Place:</strong> ${formData.place || 'Not specified'}</p>
            <div>
              <p><strong>Name:</strong> ${formData.vendor || 'Not specified'}</p>
              <p><strong>Date:</strong> ${moment(formData.date).format('DD-MM-YYYY') || 'Not specified'}</p>
            </div>
        </div>
  
        <div style="text-align: center; margin-top: 40px; color: #666; font-size: 11px;">
            This is a system-generated document from the Technical Bidding Form<br>
            Document ID: ${formData.bidNo || 'N/A'} | Reference: ${projectData?.reference || 'N/A'}
        </div>
    </div>
</body>
</html>
  `;

  return htmlContent;
};

// Main BiddingForm Component with safe data initialization
const BiddingForm = ({
  formData,
  setFormData,
  projectData,
  isPublic,
  handleSubmit,
  handleSave,
  isViewMode,
  formId
}) => {
  const { formid } = useParams();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [localPlace, setLocalPlace] = useState(formData.place || '');
  const itemsContainerRef = useRef(null);
  const [bidClick, setBidClick] = useState(false);

  const debouncedPlaceChange = useDebounce((value) => {
    setFormData(prev => ({ ...prev, place: value }));
  }, 300);

  const handlePlaceChange = useCallback((e) => {
    const value = e.target.value;
    setLocalPlace(value);
    debouncedPlaceChange(value);
  }, [debouncedPlaceChange]);

  // Default label for the first checkbox
  const defaultLabel = useMemo(() =>
    `I confirm that I have received, reviewed, and understood the RFP and any related addenda for equipment procurement/installation for ${projectData?.name || 'the project'}.`,
    [projectData?.name]
  );

  const handleDownloadResponse = useCallback(() => {
    try {
      setBidClick(false);
      // Create a structured document for download
      const docContent = generateResponseDocument(formData, projectData);

      // Convert to blob and download
      const blob = new Blob([docContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Technical_Response_${formData.bidNo || 'Response'}_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotification('Response downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download response', 'error');
    }
  }, [formData, projectData]);

  // Scroll to item function
  const scrollToItem = useCallback((element) => {
    if (element && itemsContainerRef.current) {
      const container = itemsContainerRef.current;
      const elementTop = element.offsetTop;
      const containerTop = container.offsetTop;

      // Calculate scroll position to show the item at top
      const scrollTo = elementTop - containerTop - 20; // 20px padding from top

      container.parentElement?.scrollTo({
        top: scrollTo,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleToggle = useCallback((itemId, isExpanded) => {
    setExpandedItems(prev => {
      // Close all other accordions when opening one
      if (isExpanded) {
        return { [itemId]: true };
      } else {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      }
    });
  }, []);

  const handleItemAction = useCallback((itemId, action, data) => {
    setFormData(prev => {
      const updateItem = (item) => {
        if (item.id !== itemId) return item;

        switch (action) {
          case 'purpose':
            return {
              ...item,
              sections: (item.sections || []).map(section =>
                section.type === 'purpose'
                  ? { ...section, description: data }
                  : section
              )
            };

          case 'addSection':
            const sectionTitles = [
              'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
              'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
            ];
            const existingTitles = (item.sections || []).map(s => s.title).filter(Boolean);
            const nextTitle = sectionTitles.find(title => !existingTitles.includes(title));

            if (!nextTitle) return item;

            return {
              ...item,
              sections: [
                ...(item.sections || []),
                {
                  id: Date.now() + Math.random(),
                  type: 'custom',
                  isdefault: false,
                  title: nextTitle,
                  sectionName: '',
                  requirements: [] // Initialize empty requirements array
                }
              ]
            };

          case 'updateSection':
            return {
              ...item,
              sections: (item.sections || []).map(section =>
                section.id === data.sectionId
                  ? { ...section, sectionName: data.value }
                  : section
              )
            };

          case 'removeSection':
            return {
              ...item,
              sections: (item.sections || []).filter(section => section.id !== data)
            };

          case 'addRequirement':
            const targetSection = (item.sections || []).find(s => s.id === data);
            if (!targetSection) return item;

            return {
              ...item,
              sections: (item.sections || []).map(section =>
                section.id === data
                  ? {
                    ...section,
                    requirements: [
                      ...(section.requirements || []),
                      {
                        id: Date.now() + Math.random(),
                        code: `${section.title}.${(section.requirements || []).length + 1}`,
                        description: '',
                        response: '',
                        remarks: '',
                        responseType: 'dropdown'
                      }
                    ]
                  }
                  : section
              )
            };

          case 'updateRequirement':
            return {
              ...item,
              sections: (item.sections || []).map(section => ({
                ...section,
                requirements: (section.requirements || []).map(req =>
                  req.id === data.requirementId && section.id === data.sectionId
                    ? { ...req, [data.field]: data.value }
                    : req
                )
              }))
            };

          case 'removeRequirement':
            return {
              ...item,
              sections: (item.sections || []).map(section => ({
                ...section,
                requirements: (section.requirements || [])
                  .filter(req => req.id !== data)
                  .map((req, index) => ({
                    ...req,
                    code: `${section.title}.${index + 1}`
                  }))
              }))
            };

          default:
            return item;
        }
      };

      return {
        ...prev,
        items: prev.items.map(updateItem)
      };
    });
  }, [setFormData]);

  // Handle acknowledgement changes
  const handleAcknowledgementChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      acknowledgement: {
        ...prev.acknowledgement,
        [field]: value
      }
    }));
  }, [setFormData]);

  // Handle label edit
  const handleLabelEdit = useCallback(() => {
    setIsEditingLabel(true);
  }, []);

  // Handle label save
  const handleLabelSave = useCallback(() => {
    setIsEditingLabel(false);
    if (!formData?.acknowledgement?.reviewedLabel?.trim()) {
      handleAcknowledgementChange('reviewedLabel', defaultLabel);
    }
  }, [formData?.acknowledgement?.reviewedLabel, handleAcknowledgementChange, defaultLabel]);

  // Get the current label text (use default if empty)
  const getCurrentLabel = useCallback(() => {
    return formData.acknowledgement?.reviewedLabel || defaultLabel;
  }, [formData.acknowledgement?.reviewedLabel, defaultLabel]);

  // Safe array access for items
  const items = formData.items || [];

  const renderedItems = useMemo(() =>
    items.map((item) => (
      <ItemComponent
        key={item.id}
        item={item}
        isExpanded={!!expandedItems[item.id]}
        onToggle={handleToggle}
        onItemAction={handleItemAction}
        scrollToItem={scrollToItem}
        isPublic={isPublic}
        isViewMode={isViewMode}
        setFormData={setFormData}
        formId={formId}
      />
    )), [items, expandedItems, handleToggle, handleItemAction, scrollToItem]
  );

  return (
    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        {isPublic && (
          <>
            <Typography variant='h5' style={{ fontWeight: 'bold' }}>Technical Specification Form</Typography>
            <br />
          </>
        )}
        <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <Table sx={{ minWidth: 800 }} size="small">
            <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '5%' }}>BID GROUP</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '5%' }}>BID NO</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '40%' }}>Bid Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '20%' }}>Vendor Response</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '24%' }}>Vendor Remarks in details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '6%' }}>Vendor Attachments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ p: 1 }}>
                    {formData.bidGroup}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ p: 1 }}>
                    {formData.bidNo}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ p: 1 }}>
                    {formData.bidName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {isPublic && (
          <Typography color='primary' variant='body2'>* Click on plus icon to add your response</Typography>
        )}
        {/* Items Section with ref for scrolling */}
        <TableContainer
          ref={itemsContainerRef}
          component={Paper}
          sx={{
            mb: 3,
            border: '1px solid #e0e0e0',
            maxHeight: '70vh',
            overflow: 'auto'
          }}
        >
          <Table sx={{ minWidth: 800 }} size="small">
            <TableBody>
              {/* Fixed Header Row */}
              <TableRow>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '6%', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Sr. No</TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '5.7%', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}></TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '32.5%', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name of Item</TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '5%', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Qty</TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '19.3%', backgroundColor: '#f5f5f5' }}></TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '23.2%', backgroundColor: '#f5f5f5' }}></TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', width: '10%', backgroundColor: '#f5f5f5' }}></TableCell>
              </TableRow>
              {renderedItems}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <Table sx={{ minWidth: 800 }} size="small">
            <TableHead sx={{ backgroundColor: '#f3e5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '90%' }}>Vendor Documentation Attachment</TableCell>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0', width: '10%' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(formData.vendorDocuments || []).map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                    {doc.isEditable ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={doc.name || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            vendorDocuments: (prev.vendorDocuments || []).map(document =>
                              document.id === doc.id ? { ...document, name: e.target.value } : document
                            )
                          }));
                        }}
                        placeholder="Enter document name"
                        variant="outlined"
                      />
                    ) : (doc.name || '')}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center' }}>
                    {isPublic ? (
                      <Box sx={{ position: 'relative' }}>
                        <input
                          accept=".pdf"
                          style={{ display: 'none' }}
                          id={`file-upload-${doc.id}`}
                          type="file"
                          multiple={doc.attachment > 1}
                          onChange={async (event) => {
                            const files = event.target.files;
                            if (!files || files.length === 0) return;

                            // Check attachment limit
                            const currentAttachmentCount = doc.attachments?.length || 0;
                            const maxAllowed = doc.attachment || 1;
                            const remainingSlots = maxAllowed - currentAttachmentCount;

                            if (files.length > remainingSlots) {
                              showNotification(`You can only upload ${remainingSlots} more file(s). Maximum ${maxAllowed} file(s) allowed.`, 'error');
                              event.target.value = '';
                              return;
                            }

                            const validFileTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg',
                              'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                            const maxSize = 5 * 1024 * 1024; // 5MB

                            // Validate all files
                            for (let file of files) {
                              if (!validFileTypes.includes(file.type)) {
                                showNotification('Please upload PDF, DOC, DOCX, JPG, or PNG files only.', 'error');
                                event.target.value = '';
                                return;
                              }

                              if (file.size > maxSize) {
                                showNotification('File size must be less than 5MB', 'error');
                                event.target.value = '';
                                return;
                              }
                            }

                            // Set uploading state only for this document
                            setFormData(prev => ({
                              ...prev,
                              vendorDocuments: (prev.vendorDocuments || []).map(document =>
                                document.id === doc.id
                                  ? { ...document, uploading: true }
                                  : document
                              )
                            }));

                            try {
                              // Upload all selected files
                              const uploadPromises = Array.from(files).map(async (file) => {
                                const formData = new FormData();
                                formData.append('public_files', file);
                                const response = await publicApiRequest('post', '/public-file-upload', formData);
                                return response?.data?.data?.[0];
                              });

                              const uploadedFiles = await Promise.all(uploadPromises);
                              const successfulUploads = uploadedFiles.filter(file => file !== undefined);

                              if (successfulUploads.length > 0) {
                                // Update the document with new attachments
                                setFormData(prev => ({
                                  ...prev,
                                  vendorDocuments: (prev.vendorDocuments || []).map(document =>
                                    document.id === doc.id
                                      ? {
                                        ...document,
                                        attachments: [...(document.attachments || []), ...successfulUploads],
                                        uploading: false
                                      }
                                      : document
                                  )
                                }));
                              }
                            } catch (error) {
                              console.error('Upload error:', error);
                              alert('Failed to upload file. Please try again.');
                              // Reset uploading state on error
                              setFormData(prev => ({
                                ...prev,
                                vendorDocuments: (prev.vendorDocuments || []).map(document =>
                                  document.id === doc.id
                                    ? { ...document, uploading: false }
                                    : document
                                )
                              }));
                            } finally {
                              event.target.value = '';
                            }
                          }}
                          disabled={doc.uploading || (doc.attachments?.length || 0) >= (doc.attachment || 1)}
                        />

                        {/* Show upload button only if there are available slots */}
                        {(doc.attachments?.length || 0) < (doc.attachment || 1) && (
                          <label htmlFor={`file-upload-${doc.id}`}>
                            <IconButton
                              component="span"
                              size="small"
                              color="primary"
                              disabled={doc.uploading || (doc.attachments?.length || 0) >= (doc.attachment || 1)}
                            >
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        )}

                        {/* Loading indicator only for this document */}
                        {doc.uploading && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        )}

                        {/* Display uploaded files with remove functionality */}
                        {doc.attachments && doc.attachments.map((attachment) => (
                          <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Tooltip title={`Download ${attachment.fileName}`}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const fileUrl = `${process.env.REACT_APP_IMAGEURL}bbn=${attachment.sortUrl}&uid=${formid || formId}`;
                                  const link = document.createElement('a');
                                  link.href = fileUrl;
                                  link.target = '_blank';  // Open in new tab
                                  link.rel = 'noopener noreferrer'; // Security best practice
                                  link.download = attachment.fileName;
                                  link.click();
                                }}
                              >
                                <img src='/download.png' alt='download' style={{ width: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Remove ${attachment.fileName}`}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    vendorDocuments: (prev.vendorDocuments || []).map(document =>
                                      document.id === doc.id
                                        ? {
                                          ...document,
                                          attachments: document.attachments?.filter(att => att.id !== attachment.id) || []
                                        }
                                        : document
                                    )
                                  }));
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}

                        {/* Show attachment count */}
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          {(doc.attachments?.length || 0)}/{(doc.attachment || 1)} files
                        </Typography>
                      </Box>
                    ) : isViewMode && (
                      <Box>
                        {doc.attachments && doc.attachments.map((attachment) => (
                          <Box key={attachment.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Tooltip title={`Download ${attachment.fileName}`}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const fileUrl = `${process.env.REACT_APP_IMAGEURL}bbn=${attachment.sortUrl}&uid=${formid || formId}`;
                                  const link = document.createElement('a');
                                  link.href = fileUrl;
                                  link.target = '_blank';  // Open in new tab
                                  link.rel = 'noopener noreferrer'; // Security best practice
                                  link.download = attachment.fileName;
                                  link.click();
                                }}
                              >
                                <img src='/download.png' alt='download' style={{ width: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {(!isPublic && !isViewMode) && (
                      <Tooltip title="Remove Document">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              vendorDocuments: (prev.vendorDocuments || []).filter(document => document.id !== doc.id)
                            }));
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(!isPublic && !isViewMode) && (
                <TableRow>
                  <TableCell colSpan={2} sx={{ border: '1px solid #e0e0e0' }}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          vendorDocuments: [
                            ...(prev.vendorDocuments || []),
                            {
                              id: Date.now(),
                              name: '',
                              isEditable: true,
                              attachments: [],
                              attachment: 1,
                              uploading: false // Initialize uploading state
                            }
                          ]
                        }));
                      }}
                      variant="outlined"
                      sx={{ mt: 1, mb: 1 }}
                    >
                      Add New Document
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Acknowledgement Section */}
        <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <Table sx={{ minWidth: 800 }} size="small">
            <TableHead sx={{ backgroundColor: '#e8f5e8' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0' }}>Acknowledgement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: '1px solid #e0e0e0', py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <Checkbox
                      checked={formData.acknowledgement?.reviewed || false}
                      onChange={(e) => handleAcknowledgementChange('reviewed', e.target.checked)}
                      sx={{ mt: 0.5 }}
                      disabled={isViewMode}
                    />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: '40px', marginTop: '5px' }}>
                      {isEditingLabel ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={formData.acknowledgement?.reviewedLabel || ''}
                          onChange={(e) => handleAcknowledgementChange('reviewedLabel', e.target.value)}
                          onBlur={handleLabelSave}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleLabelSave();
                            }
                          }}
                          placeholder={defaultLabel}
                          variant="standard"
                          multiline
                          autoFocus
                        />
                      ) : (
                        <Typography variant="body2" style={{ fontSize: 15 }}>
                          {getCurrentLabel()}
                        </Typography>
                      )}
                      {(!isPublic && !isViewMode) && (
                        <Tooltip title={isEditingLabel ? "Save changes" : "Edit label"}>
                          <IconButton
                            size="small"
                            onClick={isEditingLabel ? handleLabelSave : handleLabelEdit}
                            color={isEditingLabel ? "primary" : "default"}
                          >
                            {isEditingLabel ? (<Save size="small" />) : (<EditIcon fontSize="small" />)}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: '1px solid #e0e0e0', py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <Checkbox
                      checked={formData.acknowledgement?.accepted || false}
                      onChange={(e) => handleAcknowledgementChange('accepted', e.target.checked)}
                      sx={{ mt: 0.5 }}
                      disabled={isViewMode}
                    />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: '40px', marginTop: '5px' }}>
                      <Typography variant="body2" style={{ fontSize: 15 }}>
                        I accept the terms and conditions unless otherwise stated and warrant that I am authorized to submit this proposal on behalf of my organization.
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Place Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, p: 2, backgroundColor: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, backgroundColor: 'white' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 80 }}>
              Place {!isViewMode ? '*' : ''}
            </Typography>
            <TextField
              size="small"
              value={localPlace}
              onChange={handlePlaceChange}
              variant="standard"
              placeholder="Enter place"
              disabled={isViewMode}
            />
          </Box>
          {isViewMode && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, backgroundColor: 'white' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 80 }}>
                  Name
                </Typography>
                <TextField
                  size="small"
                  value={formData.vendor}
                  variant="standard"
                  placeholder="Enter place"
                  disabled={isViewMode}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, backgroundColor: 'white' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 80 }}>
                  Date
                </Typography>
                <TextField
                  size="small"
                  value={moment(formData?.date).format('DD-MM-YYYY')}
                  variant="standard"
                  placeholder="Enter place"
                  disabled={isViewMode}
                />
              </Box>
            </Box>
          )}
        </Box>

        {!isViewMode && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
            <Button variant="outlined" size="large" sx={{ minWidth: 120 }} disabled={bidClick} onClick={() => {
              if (isPublic) {
                setBidClick(true);
                handleSave()
              }
            }}>
              Save
            </Button>
            <Button variant="contained" size="large" sx={{ minWidth: 120 }} disabled={bidClick} onClick={() => {
              if (isPublic) {
                setBidClick(true);
                handleSubmit()
              }
            }}>
              {bidClick ? 'Submitted' : 'Submit'}
            </Button>
            {(bidClick && isPublic) && (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                sx={{ minWidth: 120 }}
                onClick={handleDownloadResponse}
              >
                Download Response
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BiddingForm;